import React, { useEffect, useState, useRef, useCallback } from 'react';
import StoreX from '../../redux/oldStore';
import Icon, { IconType } from '../Icon/Icon';
import * as PDFJS from 'pdfjs-dist';
import type { PDFDocumentProxy, RenderParameters } from 'pdfjs-dist/types/src/display/api';
import './PdfStyle.css';
import PdfBasicToolbar from './PdfBasicToolbar';
import PdfInput from './PdfInput';
import { IFieldInfo, ISigner } from '../GenericForms/formTypes';
import { toast } from 'react-toastify';
import PdfFooterToolbar from './PdfFooterToolbar';
import PdfViewerPage from './PdfViewerPage';
import PdfSigningSidebar, { ISigningElement, ISigningDefaults } from './PdfSigningSidebar';
import SigningElement from './SigningElement';
import { set } from 'lodash';
import { PDFDocument, rgb } from 'pdf-lib';
import { NewGuid } from '../../utils/Tools';

interface IPdfViewerProps {
  id: string;
  source?: string;
  values?: any;
  updateValues?: Function;
  close?: Function;
  fieldMetaData?: IFieldInfo[];
  signer?: ISigner;
  signRequested?: Function;
  downloadFormUrl?: string;
  viewOnly?: boolean;
  isSetupMode?: boolean;
  fieldSelected?: (field: IFieldInfo) => void;
  fieldHighlighed?: IFieldInfo;
  removeFile?: () => void;
  allowSigning?: boolean;
  pdfUpdated?: (pdf: Blob) => void;
  signingDefaults?: ISigningDefaults;
}

const PdfViewer = (props: IPdfViewerProps) => {
  PDFJS.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.9.155/build/pdf.worker.min.mjs';

  const [pdfRef, setPdfRef] = useState<any>();
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.5);
  const [zoom, setZoom] = useState(1);
  const [formValues, setFormValues] = useState<any>({});
  const [formValuesSet, setFormValuesSet] = useState<any>({});
  const [signer, setSigner] = useState<ISigner>();
  const [missingFields, setMissingFields] = useState<Record<string, IFieldInfo>>({});
  const [pages, setPages] = useState<any[]>([]);
  const [fieldsMetaData, setFieldsMetaData] = useState<any>();
  const [signingElements, setSigningElements] = useState<ISigningElement[]>([]);
  const [selectedElement, setSelectedElement] = useState<ISigningElement | null>(null);

  const [placementMode, setPlacementMode] = useState<{
    isActive: boolean;
    elementType: ISigningElement['type'] | null;
    previewPosition: { x: number; y: number };
  }>({
    isActive: false,
    elementType: null,
    previewPosition: { x: 0, y: 0 },
  });

  const [signingDefaults, setSigningDefaults] = useState<ISigningDefaults>(() => {
    const today = new Date().toISOString().split('T')[0];
    return (
      props.signingDefaults || {
        defaultDate: today,
        defaultSignature: '',
        textColor: '#000000',
      }
    );
  });

  useEffect(() => {
    setFormValues(props.values || {});
    let fmd = {};
    if (props.fieldMetaData) {
      props.fieldMetaData.forEach((x) => {
        if (x.FormKey) fmd[x.FormKey] = x;
      });
    }
    setFieldsMetaData(fmd);
  }, [props.id, props.fieldMetaData, props.values]);

  useEffect(() => {
    setSigner(props.signer);
  }, [props.signer]);

  const renderPage = useCallback(
    async (pageNum, pdf = pdfRef) => {
      if (pdf) {
        let xPages: any[] = [];
        for (let i = 0; i < pdf.numPages; i++) {
          await pdf.getPage(i + 1).then(function (page) {
            if (i === 0) setPages([page]);
            xPages.push(page);
          });
        }
        setPages(xPages);
      }
      console.warn('Render Page', pageNum, pdf);
    },
    [pdfRef]
  );

  useEffect(() => {
    if (pdfRef) renderPage(currentPage, pdfRef);
  }, [pdfRef, currentPage, renderPage, props.id]);

  useEffect(() => {
    const fullUrl = StoreX.BuildUrl(props.source ?? '');
    const loadingTask = PDFJS.getDocument(props.source.replace('../', '/'));
    loadingTask.promise.then(
      (loadedPdf) => {
        setPdfRef(loadedPdf);
      },
      function (reason) {
        console.error(reason);
        console.log(fullUrl);
        toast.error(`Error loading PDF: ${reason}`);
      }
    );
  }, [props.source]);

  const inputValueChanged = (id: string, value: string) => {
    let newValues = { ...formValues, [id]: value };
    setFormValues(newValues);

    let valuesSet = { ...formValuesSet, [id]: value };
    setFormValuesSet(valuesSet);
  };

  const handleAddSigningElement = (type: ISigningElement['type']) => {
    setPlacementMode({
      isActive: true,
      elementType: type,
      previewPosition: { x: 0, y: 0 },
    });
    console.log('Entering placement mode for type:', type);
    setSelectedElement(null);
  };

  const handleApplySignatures = async () => {
    if (!pdfRef || signingElements.length === 0) {
      toast.warning('No signatures to apply');
      return;
    }

    try {
      toast.info('Generating signed PDF...');

      // Fetch the original PDF data
      const pdfData = await fetch(props.source.replace('../', '/')).then(res => res.arrayBuffer());
      
      // Load the PDF with pdf-lib
      const pdfDoc = await PDFDocument.load(pdfData);
      const pdfPages = pdfDoc.getPages();

      // Group elements by page
      const elementsByPage: Record<number, ISigningElement[]> = {};
      signingElements.forEach(el => {
        if (!elementsByPage[el.pageNumber]) {
          elementsByPage[el.pageNumber] = [];
        }
        elementsByPage[el.pageNumber].push(el);
      });

      // Apply elements to each page
      for (const [pageNumStr, elements] of Object.entries(elementsByPage)) {
        const pageNum = parseInt(pageNumStr) - 1; // Convert to 0-based index
        if (pageNum < 0 || pageNum >= pdfPages.length) continue;

        const page = pdfPages[pageNum];
        const { width: pdfWidth, height: pdfHeight } = page.getSize();

        // Get the corresponding PDFJS page to calculate the scale
        const pdfjsPage = pages[pageNum];
        if (!pdfjsPage) continue;

        // Calculate the viewport used for rendering
        let viewport = pdfjsPage.getViewport({ scale });
        if (viewport.width > 1000) {
          const w = viewport.width / scale;
          const s = 950 / w;
          viewport = pdfjsPage.getViewport({ scale: s });
        }

        // Calculate the scale ratio between canvas and PDF
        const scaleX = pdfWidth / viewport.width;
        const scaleY = pdfHeight / viewport.height;

        for (const element of elements) {
          // Convert canvas coordinates to PDF coordinates
          // Canvas coordinates are scaled, so we need to scale them to PDF size
          const pdfX = element.x * scaleX;
          const pdfY = pdfHeight - (element.y * scaleY) - 14; // 14 is approximate text height
          
          // Parse color
          const color = element.textColor || '#000000';
          const r = parseInt(color.slice(1, 3), 16) / 255;
          const g = parseInt(color.slice(3, 5), 16) / 255;
          const b = parseInt(color.slice(5, 7), 16) / 255;

          // Determine content to draw
          let textContent = '';
          let fontSize = 10;

          switch (element.type) {
            case 'signature':
              textContent = element.content || 'Signature';
              fontSize = 10;
              break;
            case 'date':
              textContent = new Date().toLocaleDateString();
              fontSize = 10;
              break;
            case 'checkmark':
              // Use ASCII-compatible character instead of Unicode
              textContent = '[X]';
              fontSize = 10;
              break;
            case 'x':
              // Use ASCII-compatible character instead of Unicode
              textContent = 'X';
              fontSize = 10;
              break;
            case 'text':
              textContent = element.content || 'Text';
              fontSize = 10;
              break;
          }

          // Draw text on the PDF - only draw if content is ASCII-compatible
          try {
            page.drawText(textContent, {
              x: pdfX,
              y: pdfY+3.5,
              size: fontSize,
              color: rgb(r, g, b),
            });
          } catch (encodeError) {
            // If encoding fails, try to sanitize the text to ASCII
            console.warn(`Failed to encode "${textContent}", attempting sanitization`, encodeError);
            // eslint-disable-next-line no-control-regex
            const sanitizedText = textContent.replace(/[^\x00-\x7F]/g, '?');
            page.drawText(sanitizedText, {
              x: pdfX,
              y: pdfY,
              size: fontSize,
              color: rgb(r, g, b),
            });
          }
        }
      }

      // Save the modified PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });

      // Call the callback with the new PDF
      if (props.pdfUpdated) {
        props.pdfUpdated(blob);
        toast.success('Signatures applied successfully!');
        
        // Clear all signing elements after successful flattening
        setSigningElements([]);
        setSelectedElement(null);
      }
    } catch (error) {
      console.error('Error applying signatures:', error);
      toast.error('Failed to apply signatures to PDF');
    }
  };

  const saveClick = (save: boolean, close: boolean) => {
    if (props.signer) {
      //this is to submit signed stuff...
      if (save && props.updateValues) {
        //was everything signed that needed to be?
        let signerFields = props?.fieldMetaData?.filter((x) => x.SignatureOwner === props.signer?.SignatureInfoId && x.Type !== 'Btn' && !x.Hidden) ?? [];

        let missing = signerFields.filter((x) => !formValuesSet[x.FormKey ?? '|#|'] && !formValues[x.FormKey ?? '|#|']);
        missing = missing.filter((x) => !x.SignatureFieldNotRequired);
        let missingDic: Record<string, IFieldInfo> = {};
        missing.forEach((x) => {
          missingDic[x.PublicId] = x;
        });
        setMissingFields(missingDic);

        let valuesToSave = { ...formValuesSet };

        signerFields.forEach((x) => {
          if (!valuesToSave[x.FormKey ?? '|#|']) {
            valuesToSave[x.FormKey ?? '|#|'] = formValues[x.FormKey ?? '|#|'];
          }
        });

        if (missing.length > 0) {
          toast.warning(`You have not filled out / signed all of the information you need to on this page.`);
          console.log('Missing Fields:', missing);
          missing.forEach((x) => {
            console.log(`""${x.FormKey}""`, x.Label, x.Type, x.FormKey, x.Hidden);
            console.log(x);
            console.log(formValuesSet);
            console.log(formValues);
          });
          //if(!window.confirm('Are you sure you want to submit? You have not filled everything out yet.'))
          return;
        }

        props.updateValues(props.id, valuesToSave, close);
        setFormValuesSet({});
        return;
      }

      if (close && props.close) {
        if (Object.keys(formValuesSet).length > 0 && !window.confirm(`If you close now your signature will not be saved. Are you sure you want to close?`)) {
          return;
        }
        props.close();
      }
    }
    if (save && props.updateValues) {
      props.updateValues(props.id, formValues, close);
      return;
    }
    if (close && props.close) {
      props.close();
    }
  };

  const signingRequested = (signatureOwner: string) => {
    if (props.signRequested) props.signRequested(signatureOwner);
  };


  return (
    <>
      <div className="zpdf">
        <div className="zpdf-desk">
          {props.allowSigning && (
            <PdfSigningSidebar
              selectedElement={selectedElement}
              onAddElement={handleAddSigningElement}
              onDeleteElement={() => {
                if (selectedElement) {
                  let elements = signingElements.filter((el) => el.id !== selectedElement.id);
                  setSigningElements(elements);
                  setSelectedElement(null);
                }
              }}
              signingDefaults={signingDefaults}
              onDefaultsChange={setSigningDefaults}
              onApplySignatures={props.pdfUpdated ? handleApplySignatures : undefined}
              hasSigningElements={signingElements.length > 0}
            />
          )}
          <div className="zpdf-container">
            <PdfBasicToolbar
              page={currentPage}
              // pages={pdfRef?.numPages ?? 0}
              pages={0}
              viewOnly={props.viewOnly ?? false}
              changePage={(n) => {
                setCurrentPage(n);
              }}
              zoom={zoom}
              changeZoom={(n) => {
                setZoom(n);
              }}
              save={saveClick}
              isSigning={signer ? true : false}
              downloadFormUrl={props.downloadFormUrl}
              removeFile={props.removeFile}
            />
            <div className={`zpdf-viewer`}>
              {pages &&
                fieldsMetaData &&
                pages.map((p, i) => {
                  const pageNumber = i + 1;
                  const pageElements = signingElements.filter((el) => el.pageNumber === pageNumber);

                  return (
                    <div key={`pdf-page-container-${i}`} className={`zpdf-page-container scale=${scale}`}>
                      <PdfViewerPage
                        id={props.id}
                        key={`pdf-v-p-k-${i}`}
                        viewOnly={props.viewOnly ?? false}
                        scale={scale}
                        inputValueChanged={inputValueChanged}
                        formValues={formValues}
                        fieldsMetaData={fieldsMetaData}
                        missingFields={missingFields}
                        signingRequested={signingRequested}
                        isSetupMode={props.isSetupMode ?? false}
                        page={p}
                        scrollTo={pageNumber === currentPage}
                        signer={signer}
                        fieldSelected={(field: any) => {
                          if (props.fieldSelected) {
                            props.fieldSelected(field);
                          } else {
                            console.warn('Method not definded for fieldSelected');
                          }
                        } }
                        fieldHighlighed={props.fieldHighlighed}
                        signingElements={props.allowSigning ? pageElements : []}
                        signingElementUpdated={(element: ISigningElement) => {
                          let elements = signingElements.filter((el) => el.id !== element.id);
                          elements.push(element);
                          setSigningElements(elements);
                        } }
                        signingElemenetSelected={(element: ISigningElement | null) => {
                          setSelectedElement(element);
                        } }
                        signingDefaults={signingDefaults}
                        allowSigning={props.allowSigning} 
                        signingDefaultsChanged={(x)=>{setSigningDefaults(x)}} 
                        signingElementRemoved={(x)=>{
                          let elements = signingElements.filter((el) => el.id !== x.id);
                          setSigningElements(elements);
                        }}
                        placementMode={placementMode}
                        setPlacementMode={setPlacementMode}
                        
                        />
                    </div>
                  );
                })}
            </div>

            <PdfFooterToolbar
              page={currentPage}
              // pages={pdfRef?.numPages ?? 0}
              pages={0}
              changePage={(n) => {
                setCurrentPage(n);
              }}
              zoom={zoom}
              changeZoom={(n) => {
                setZoom(n);
              }}
              save={saveClick}
              isSigning={signer ? true : false}
              downloadFormUrl={props.downloadFormUrl}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PdfViewer;
